import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { X } from "lucide-react";
import { STATUS_OPTIONS, type StatusId } from "#shared/constants.ts";
import { Button } from "@web/components/ui/button.tsx";
import { useCreateGenre, useGenresFlat, useLocalPlaylists } from "@web/features/library/api.ts";
import {
  useBulkDriveUrl,
  useBulkGenres,
  useBulkPlaylist,
  useBulkStatus,
} from "@web/features/library/bulkApi.ts";
import { useSelectionStore } from "@web/stores/selection.ts";
import { cn } from "@web/lib/utils.ts";

const NEW_GENRE = "__new__";

/**
 * Always mounted so the grid doesn't jump when selection appears/clears.
 * Idle (no selection): dimmed and non-interactive.
 */
export function ActionBar({ className }: { className?: string }) {
  const count = useSelectionStore((s) => s.count());
  const mode = useSelectionStore((s) => s.mode);
  const clear = useSelectionStore((s) => s.clear);
  const bulkTarget = useSelectionStore((s) => s.bulkTarget);
  const local = useLocalPlaylists();
  const genres = useGenresFlat();
  const [searchParams] = useSearchParams();
  const assignGenreId = searchParams.get("assignGenre");
  const setStatus = useBulkStatus();
  const setPlaylist = useBulkPlaylist();
  const setDrive = useBulkDriveUrl();
  const setGenres = useBulkGenres();
  const createGenre = useCreateGenre();
  const [playlistId, setPlaylistId] = useState("");
  const [genreId, setGenreId] = useState("");
  const [newGenreName, setNewGenreName] = useState("");
  const [driveOpen, setDriveOpen] = useState(false);
  const [driveDraft, setDriveDraft] = useState("");
  /** Blocks Add for one tick after Create so Enter can’t hit a swapped Add button. */
  const blockAddUntilRef = useRef(0);

  const target = bulkTarget();
  const active = count > 0 && target !== null;

  useEffect(() => {
    if (assignGenreId) setGenreId(assignGenreId);
  }, [assignGenreId]);

  const busy =
    setStatus.isPending ||
    setPlaylist.isPending ||
    setDrive.isPending ||
    setGenres.isPending ||
    createGenre.isPending;
  const controlsDisabled = !active || busy;
  const creatingGenre = genreId === NEW_GENRE;

  function applyDrive() {
    if (!target) return;
    setDrive.mutate(
      { drive_url: driveDraft.trim() === "" ? null : driveDraft.trim(), target },
      {
        onSuccess: () => {
          setDriveOpen(false);
          setDriveDraft("");
        },
      },
    );
  }

  function applyGenre(id: string) {
    if (!target) return;
    setGenres.mutate({ add: [id], target });
  }

  function createThenSelect() {
    const name = newGenreName.trim();
    if (!name) return;
    createGenre.mutate(name, {
      onSuccess: (genre) => {
        // Enter can synthesize a click on the Create control; if we swap Create→Add
        // in the same event turn, that click commits the assignment. Defer select
        // and ignore Add briefly so Create only creates.
        blockAddUntilRef.current = Date.now() + 500;
        window.setTimeout(() => {
          setGenreId(genre.id);
          setNewGenreName("");
        }, 0);
      },
    });
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3 border-b border-line bg-panel-elevated px-4 py-2 transition-opacity",
        !active && "opacity-50",
        className,
      )}
      role="toolbar"
      aria-label="Selection actions"
      aria-disabled={!active}
      inert={!active ? true : undefined}
    >
      <span className="text-sm font-semibold tabular-nums">
        {count.toLocaleString()} selected
        {mode?.kind === "filter" ? " (all matching)" : ""}
      </span>

      <label className="flex items-center gap-2 text-sm text-muted">
        Status
        <select
          className="h-8 rounded-md border border-line bg-panel px-2 text-text"
          disabled={controlsDisabled}
          defaultValue=""
          onChange={(e) => {
            if (!target) return;
            const status = e.target.value as StatusId;
            if (!status) return;
            setStatus.mutate({ status, target });
            e.target.value = "";
          }}
        >
          <option value="" disabled>
            Set…
          </option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      <div className="flex items-center gap-2 text-sm text-muted">
        <span>Genre</span>
        <select
          className="h-8 max-w-48 rounded-md border border-line bg-panel px-2 text-text"
          disabled={controlsDisabled}
          value={genreId}
          aria-label="Genre"
          onChange={(e) => {
            setGenreId(e.target.value);
            if (e.target.value !== NEW_GENRE) setNewGenreName("");
          }}
        >
          <option value="">Choose…</option>
          {(genres.data ?? []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
          <option value={NEW_GENRE}>New genre…</option>
        </select>
        {creatingGenre && (
          <input
            type="text"
            value={newGenreName}
            onChange={(e) => setNewGenreName(e.target.value)}
            placeholder="Genre name"
            aria-label="New genre name"
            className="h-8 w-40 rounded-md border border-line bg-panel px-2 text-sm text-text placeholder:text-faint focus-visible:outline-none"
            autoFocus
            disabled={controlsDisabled}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                createThenSelect();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setGenreId(assignGenreId ?? "");
                setNewGenreName("");
              }
            }}
          />
        )}
        {creatingGenre && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={controlsDisabled || newGenreName.trim() === ""}
            onClick={createThenSelect}
          >
            Create
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={controlsDisabled || creatingGenre || !genreId || genreId === NEW_GENRE}
          onClick={() => {
            if (Date.now() < blockAddUntilRef.current) return;
            if (!genreId || genreId === NEW_GENRE) return;
            applyGenre(genreId);
          }}
        >
          Add
        </Button>
      </div>

      <label className="flex items-center gap-2 text-sm text-muted">
        Playlist
        <select
          className="h-8 max-w-48 rounded-md border border-line bg-panel px-2 text-text"
          disabled={controlsDisabled}
          value={playlistId}
          onChange={(e) => setPlaylistId(e.target.value)}
        >
          <option value="">Choose…</option>
          {local.data?.map((pl) => (
            <option key={pl.id} value={pl.id}>
              {pl.name}
            </option>
          ))}
        </select>
        <Button
          variant="secondary"
          size="sm"
          disabled={controlsDisabled || !playlistId}
          onClick={() => {
            if (!playlistId || !target) return;
            setPlaylist.mutate({
              local_playlist_id: playlistId,
              action: "add",
              target,
            });
          }}
        >
          Add
        </Button>
      </label>

      {driveOpen ? (
        <div className="flex items-center gap-2">
          <input
            type="url"
            value={driveDraft}
            onChange={(e) => setDriveDraft(e.target.value)}
            placeholder="Drive folder URL (empty clears)"
            className="h-8 w-64 rounded-md border border-line bg-panel px-2 text-sm text-text placeholder:text-faint focus-visible:outline-none"
            autoFocus
            disabled={controlsDisabled}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyDrive();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setDriveOpen(false);
                setDriveDraft("");
              }
            }}
          />
          <Button variant="secondary" size="sm" disabled={controlsDisabled} onClick={applyDrive}>
            Apply
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={controlsDisabled}
            onClick={() => {
              setDriveOpen(false);
              setDriveDraft("");
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          disabled={controlsDisabled}
          onClick={() => setDriveOpen(true)}
        >
          Drive URL
        </Button>
      )}

      <div className="flex-1" />

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Clear selection"
        disabled={!active}
        onClick={clear}
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}
